import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Plus, Edit2, Trash2, CheckCircle, XCircle, 
  Globe, Users, Building2, Loader2, Save, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CitiesManagement Component
 * Admin panel for managing cities/tenants
 */
export const CitiesManagement = ({ adminId }) => {
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tenant_id: '',
    name: '',
    country: 'ES',
    is_active: true
  });

  // Get admin headers for API calls
  const getHeaders = () => ({
    'X-Admin-Id': adminId,
    'X-Admin-Role': 'super_admin'
  });

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/tenants`, {
        headers: getHeaders()
      });
      setCities(response.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingCity) {
        // Update existing city
        await axios.put(`${API}/tenants/${editingCity.tenant_id}`, form, {
          headers: getHeaders()
        });
        toast.success('Ciudad actualizada');
      } else {
        // Create new city
        await axios.post(`${API}/tenants`, {
          name: form.name,
          slug: form.tenant_id,
          country: form.country,
          is_active: form.is_active
        }, {
          headers: getHeaders()
        });
        toast.success('Ciudad creada');
      }
      
      fetchCities();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setForm({
      tenant_id: city.tenant_id,
      name: city.name,
      country: city.country || 'ES',
      is_active: city.is_active !== false
    });
    setShowForm(true);
  };

  const handleToggleActive = async (city) => {
    try {
      await axios.put(`${API}/tenants/${city.tenant_id}`, {
        ...city,
        is_active: !city.is_active
      }, {
        headers: getHeaders()
      });
      fetchCities();
      toast.success(city.is_active ? 'Ciudad desactivada' : 'Ciudad activada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCity(null);
    setForm({
      tenant_id: '',
      name: '',
      country: 'ES',
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
            <Globe className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Ciudades / Tenants</h3>
            <p className="text-sm text-gray-400">{cities.length} ciudades configuradas</p>
          </div>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Ciudad
        </Button>
      </div>

      {/* Cities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cities.map((city) => (
          <motion.div
            key={city.tenant_id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-gray-800 rounded-xl p-5 border ${
              city.is_active ? 'border-gray-700' : 'border-red-900/50 opacity-60'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  city.is_active ? 'bg-emerald-500/20' : 'bg-gray-700'
                }`}>
                  <MapPin className={`w-5 h-5 ${city.is_active ? 'text-emerald-400' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h4 className="font-semibold text-white">{city.name}</h4>
                  <p className="text-sm text-gray-400">{city.tenant_id}.lokalgrow.com</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${
                city.is_active 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {city.is_active ? 'Activa' : 'Inactiva'}
              </span>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-gray-700/50 rounded-lg p-2 text-center">
                <Users className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                <p className="text-sm font-semibold text-white">{city.stats?.total_users || 0}</p>
                <p className="text-xs text-gray-400">Usuarios</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-2 text-center">
                <Building2 className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                <p className="text-sm font-semibold text-white">{city.stats?.total_businesses || 0}</p>
                <p className="text-xs text-gray-400">Negocios</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEdit(city)}
                className="flex-1 text-gray-300 border-gray-600"
              >
                <Edit2 className="w-3 h-3 mr-1" />
                Editar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleToggleActive(city)}
                className={`flex-1 ${
                  city.is_active 
                    ? 'text-red-400 border-red-800 hover:bg-red-900/20' 
                    : 'text-emerald-400 border-emerald-800 hover:bg-emerald-900/20'
                }`}
              >
                {city.is_active ? (
                  <><XCircle className="w-3 h-3 mr-1" /> Desactivar</>
                ) : (
                  <><CheckCircle className="w-3 h-3 mr-1" /> Activar</>
                )}
              </Button>
            </div>
          </motion.div>
        ))}

        {/* Empty State */}
        {cities.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Globe className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>No hay ciudades configuradas</p>
            <Button
              onClick={() => setShowForm(true)}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primera ciudad
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={resetForm}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-700"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">
                {editingCity ? 'Editar Ciudad' : 'Nueva Ciudad'}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">ID del Tenant (subdominio)</Label>
                <Input
                  value={form.tenant_id}
                  onChange={(e) => setForm({ ...form, tenant_id: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                  placeholder="madrid"
                  className="bg-gray-700 border-gray-600 text-white"
                  disabled={!!editingCity}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">
                  Será: {form.tenant_id || 'xxx'}.lokalgrow.com
                </p>
              </div>

              <div>
                <Label className="text-gray-300">Nombre de la Ciudad</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Madrid"
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-gray-300">País</Label>
                <select
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                >
                  <option value="ES">España</option>
                  <option value="DE">Alemania</option>
                  <option value="FR">Francia</option>
                  <option value="GB">Reino Unido</option>
                  <option value="IT">Italia</option>
                  <option value="PT">Portugal</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded border-gray-600 bg-gray-700"
                />
                <Label htmlFor="is_active" className="text-gray-300 cursor-pointer">
                  Ciudad activa
                </Label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="flex-1 border-gray-600 text-gray-300"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Guardar</>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default CitiesManagement;
